"use client"

import * as React from "react"
import Link from "next/link"
import Image from "next/image"
import { Menu, X, User, LogOut, LayoutDashboard } from "lucide-react"
import { DragonIcon } from "@/components/ui/DragonIcon" // Import Custom Icon
import { useSession, signOut } from "next-auth/react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { motion, AnimatePresence } from "framer-motion"

const navLinks = [
    { label: "Home", href: "/" },
    { label: "Download", href: "/download" },
    { label: "Donation", href: "/shop" },
    { label: "Rewards", href: "/rewards" },
    { label: "Vote", href: "/vote" },
    { label: "News", href: "/news" },
    { label: "Rankings", href: "/ranking" },
    { label: "Guides", href: "/guides" },
    { label: "Auras", href: "/guides/auras" },
    { label: "Appearances", href: "/guides/appearances" },
    { label: "Bosses", href: "/bosses" },
    { label: "Siege", href: "/siege" },
]

export function Navbar() {
    const [isOpen, setIsOpen] = React.useState(false)
    const [scrolled, setScrolled] = React.useState(false)
    const { data: session } = useSession()

    React.useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20)
        window.addEventListener("scroll", handleScroll)
        return () => window.removeEventListener("scroll", handleScroll)
    }, [])

    return (
        <nav
            className={cn(
                "fixed top-0 z-[100] w-full transition-all duration-300",
                scrolled
                    ? "bg-slate-950/90 backdrop-blur-xl border-b border-white/5 py-3 shadow-2xl"
                    : "bg-transparent py-6"
            )}
        >
            <div className="container mx-auto px-4">
                <div className="flex h-12 items-center justify-between">
                    <div className="flex items-center gap-12">
                        {/* Logo */}
                        <Link href="/" className="group flex items-center gap-4">
                            <DragonIcon className="text-amber-500 h-12 w-12 drop-shadow-[0_0_15px_rgba(245,158,11,0.5)] group-hover:scale-110 transition-transform duration-300" strokeWidth={2} />
                            <span className="text-3xl lg:text-4xl font-black text-white tracking-tighter uppercase italic font-cinzel drop-shadow-md">
                                DRAGON<span className="text-amber-500 text-glow">L2</span>
                            </span>
                        </Link>

                        {/* Desktop Links */}
                        <div className="hidden lg:flex items-center gap-8">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.href}
                                    href={link.href}
                                    className="relative text-[11px] font-bold text-slate-400 hover:text-white uppercase tracking-[0.15em] transition-colors group font-sans"
                                >
                                    {link.label}
                                    <span className="absolute -bottom-2 left-0 w-0 h-0.5 bg-amber-500 transition-all duration-300 group-hover:w-full box-shadow-glow" />
                                </Link>
                            ))}
                        </div>
                    </div>

                    {/* Desktop Right */}
                    <div className="hidden lg:flex items-center gap-4">
                        {session ? (
                            <div className="flex items-center gap-3 bg-slate-900/60 border border-slate-800 rounded-full pl-4 pr-1 py-1 backdrop-blur-md">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest font-sans">
                                    Welcome, <span className="text-amber-400 italic font-cinzel">{session.user?.login || "Hero"}</span>
                                </span>
                                <div className="flex items-center gap-1">
                                    <Link href="/dashboard">
                                        <Button size="sm" className="h-8 rounded-full bg-slate-800 hover:bg-slate-700 hover:text-amber-400 text-slate-200 font-bold text-[10px] uppercase border border-white/5 transition-colors">
                                            <LayoutDashboard className="w-3.5 h-3.5 mr-1" />
                                            Panel
                                        </Button>
                                    </Link>
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        onClick={() => signOut()}
                                        className="h-8 w-8 rounded-full text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                                    >
                                        <LogOut className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center gap-4">
                                <Link href="/login" className="text-[11px] font-bold text-slate-300 hover:text-amber-400 uppercase tracking-[0.2em] transition-colors font-sans">
                                    Login
                                </Link>
                                <Link href="/register">
                                    <Button size="sm" className="h-10 px-6 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white font-bold text-[11px] uppercase tracking-widest rounded transition-all shadow-lg shadow-amber-900/40 hover:shadow-amber-600/20 active:scale-95 border border-amber-500/20 font-sans">
                                        Join Now
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        className="lg:hidden p-2 text-slate-400 hover:text-amber-400 transition-colors"
                        onClick={() => setIsOpen(!isOpen)}
                    >
                        {isOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="lg:hidden bg-slate-950/95 backdrop-blur-xl border-b border-amber-900/30 overflow-hidden absolute w-full top-full left-0 shadow-2xl"
                    >
                        <div className="container mx-auto px-6 py-8 space-y-8">
                            <div className="grid gap-6">
                                {navLinks.map((link) => (
                                    <Link
                                        key={link.href}
                                        href={link.href}
                                        onClick={() => setIsOpen(false)}
                                        className="text-2xl font-black text-slate-300 hover:text-amber-500 uppercase tracking-tight font-cinzel transition-colors hover:pl-2"
                                    >
                                        {link.label}
                                    </Link>
                                ))}
                            </div>
                            <div className="h-px bg-gradient-to-r from-transparent via-slate-800 to-transparent w-full" />
                            {session ? (
                                <div className="flex flex-col gap-4">
                                    <Link href="/dashboard" onClick={() => setIsOpen(false)}>
                                        <Button className="w-full h-12 text-sm font-bold bg-slate-800 text-white border border-slate-700 uppercase tracking-widest font-sans">
                                            User Dashboard
                                        </Button>
                                    </Link>
                                    <Button variant="outline" className="w-full h-12 text-sm font-bold border-slate-800 text-slate-400 hover:text-red-400 uppercase tracking-widest font-sans" onClick={() => signOut()}>
                                        Log Out
                                    </Button>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-4">
                                    <Link href="/login" onClick={() => setIsOpen(false)}>
                                        <Button variant="outline" className="w-full h-12 text-sm font-bold border-slate-700 text-slate-300 hover:text-white uppercase tracking-widest font-sans">Log In</Button>
                                    </Link>
                                    <Link href="/register" onClick={() => setIsOpen(false)}>
                                        <Button className="w-full h-12 text-sm font-bold bg-amber-600 hover:bg-amber-500 text-white uppercase tracking-widest font-sans border-none">Register</Button>
                                    </Link>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    )
}
